import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Heading,
  useToast,
  Spinner,
  Alert,
  AlertIcon,
  Badge,
  Select,
  Button,
  Flex,
  Stack,
  useBreakpointValue
} from '@chakra-ui/react';
import axios from 'axios';
import { BASE_URL } from '../../../Globalapi/Globalapi';

const PaidList = () => {
  const [paidFees, setPaidFees] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);
  const toast = useToast();
  const studentId = sessionStorage.getItem('student_id');
  const isMobile = useBreakpointValue({ base: true, md: false });

  useEffect(() => {
    fetchPaidFees();
  }, [studentId]);

  const fetchPaidFees = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await axios.post(`${BASE_URL}/admin/Fees/fees_master/paid_fees_student.php`, {
        student_id: studentId,
      });

      if (response.data.success) {
        setPaidFees(response.data.data);
      } else {
        setError(response.data.message);
        toast({
          title: 'Error',
          description: response.data.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to fetch paid fees';
      setError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(paidFees.length / rowsPerPage);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const paginatedFees = paidFees.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minH="200px">
        <Spinner size="xl" color="blue.500" />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        {error}
      </Alert>
    );
  }

  return (
    <Box w='100%'>
      {paidFees.length === 0 ? (
        <Alert status="info">
          <AlertIcon />
          No paid fees found for this student.
        </Alert>
      ) : (
        <Box>
          <Flex
            direction={isMobile ? "column" : "row"}
            justify={isMobile ? "start" : "space-between"}
            align="center"
            className="sb"
            mb={2}
            p={3}
          >
            {/* Rows Per Page Section */}
            <Box mb={isMobile ? 4 : 0}>
              <Flex align="center">
                <Text mr={2}>Rows Per Page</Text>
                <Select
                  width="100px"
                  value={rowsPerPage}
                  onChange={(e) => setRowsPerPage(Number(e.target.value))}
                  borderColor="#E9EDF4"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </Select>
              </Flex>
            </Box>

          </Flex>
          <Box overflowX="auto">
            <Table  sx={{
                "tbody tr:nth-of-type(even)": { backgroundColor: "#f2f4f8" },
              }}>
              <Thead>
                <Tr>
                  <Th>Fee Name</Th>
                  <Th>Payment Date</Th>
                  <Th>Due Date</Th>
                  <Th>Amount</Th>
                  <Th>Fine</Th>
                  <Th>Discount</Th>
                  <Th>Total Paid</Th>
                  <Th>Payment Method</Th>
                </Tr>
              </Thead>
              <Tbody>
                {paginatedFees.map((fee) => (
                  <Tr key={fee.fee_id}>
                    <Td>
                      <Text fontWeight="medium">{fee.fee_name}</Text>
                      {fee.description && (
                        <Text fontSize="sm" color="gray.600">{fee.description}</Text>
                      )}
                    </Td>
                    <Td>{formatDate(fee.payment_date)}</Td>
                    <Td>{formatDate(fee.due_date)}</Td>
                    <Td>{formatAmount(fee.total_amount)}</Td>
                    <Td>
                      {fee.fine_amount > 0 ? (
                        <Badge colorScheme="red">{formatAmount(fee.fine_amount)}</Badge>
                      ) : '-'}
                    </Td>
                    <Td>
                      {fee.discount > 0 ? (
                        <Badge colorScheme="green">{formatAmount(fee.discount)}</Badge>
                      ) : '-'}
                    </Td>
                    <Td fontWeight="bold">{formatAmount(fee.t_total)}</Td>
                    <Td>
                      <Badge colorScheme="green" borderRadius='full'>
                        {fee.payment_method === 2
                          ? 'Online'
                          : fee.payment_method === 1
                            ? 'Cash'
                            : 'Not specified'}
                      </Badge>

                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
          <Flex justify="end" className='sb2' borderTopWidth="1px" mt={1} p={3}>
            <Button
              onClick={() => handlePageChange(currentPage - 1)}
              isDisabled={currentPage === 1}
            >
              Prev
            </Button>
            <Stack direction="row" mx={2}>
              {currentPage > 1 && (
                <>
                  <Button onClick={() => handlePageChange(1)}>1</Button>
                  {currentPage > 2 && <Text>...</Text>}
                </>
              )}
              <Button backgroundColor="teal" color="white">
                {currentPage}
              </Button>
              {currentPage < totalPages - 1 && (
                <>
                  <Text>...</Text>
                  <Button onClick={() => handlePageChange(totalPages)}>
                    {totalPages}
                  </Button>
                </>
              )}
            </Stack>
            <Button
              onClick={() => handlePageChange(currentPage + 1)}
              isDisabled={currentPage === totalPages}
            >
              Next
            </Button>
          </Flex>
        </Box>
      )}
    </Box>
  );
};

export default PaidList;
